import React, { useState, useEffect, createContext, useContext } from "react";
import { auth, db } from "./firebase";
import { GoogleAuthProvider, signInWithPopup, signOut, onAuthStateChanged } from "firebase/auth";
import { doc, getDoc, setDoc } from "firebase/firestore";
import translations from "./i18n/translations";
import BottomNav from "./components/BottomNav";
import Login from "./pages/Login";
import Browse from "./pages/Browse";
import PostListing from "./pages/PostListing";
import MyListings from "./pages/MyListings";
import Profile from "./pages/Profile";
import ListingDetail from "./pages/ListingDetail";

const AppContext = createContext();
export const useApp = () => useContext(AppContext);

export default function App() {
  const [user, setUser] = useState(null);
  const [authLoading, setAuthLoading] = useState(true);
  const [page, setPage] = useState("browse");
  const [selectedListing, setSelectedListing] = useState(null);
  const [lang] = useState("en");
  const t = translations[lang];

  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (firebaseUser) => {
      if (firebaseUser) {
        const userRef = doc(db, "users", firebaseUser.uid);
        const snap = await getDoc(userRef);
        if (!snap.exists()) {
          await setDoc(userRef, {
            name: firebaseUser.displayName || "",
            email: firebaseUser.email || "",
            whatsapp: "",
            saved: [],
          });
        }
        setUser(firebaseUser);
      } else {
        setUser(null);
      }
      setAuthLoading(false);
    });
    return () => unsub();
  }, []);

  const handleSignIn = async () => {
    const provider = new GoogleAuthProvider();
    try {
      await signInWithPopup(auth, provider);
    } catch (err) {
      console.error("Sign in error:", err);
    }
  };

  const handleSignOut = async () => {
    await signOut(auth);
    setPage("browse");
    setSelectedListing(null);
  };

  const openListing = (listing) => {
    setSelectedListing(listing);
    setPage("detail");
  };

  const closeListing = () => {
    setSelectedListing(null);
    setPage("browse");
  };

  if (authLoading) {
    return (
      <div className="splash-screen">
        <div className="splash-logo">
          <svg width="56" height="56" viewBox="0 0 56 56" fill="none">
            <rect width="56" height="56" rx="14" fill="#1a2744"/>
            <rect x="13" y="13" width="13" height="13" rx="2" fill="#f0e6c8"/>
            <rect x="30" y="13" width="13" height="13" rx="2" fill="#f0e6c8"/>
            <rect x="13" y="30" width="13" height="13" rx="2" fill="#f0e6c8"/>
            <rect x="30" y="30" width="13" height="13" rx="2" fill="#e8b800"/>
          </svg>
          <span className="splash-wordmark">kepti market</span>
        </div>
      </div>
    );
  }

  if (!user) {
    return <Login onSignIn={handleSignIn} t={t} />;
  }

  return (
    <AppContext.Provider value={{ user, t, handleSignOut, openListing }}>
      <div className="app-container">
        <div className="page-content">
          {page === "browse" && <Browse />}
          {page === "sell" && (
            <PostListing onDone={() => setPage("browse")} />
          )}
          {page === "mylistings" && (
            <MyListings onOpenListing={openListing} />
          )}
          {page === "profile" && <Profile />}
          {page === "detail" && selectedListing && (
            <ListingDetail
              listing={selectedListing}
              onBack={closeListing}
              onUpdated={(updated) => setSelectedListing(updated)}
            />
          )}
        </div>
        {page !== "detail" && (
          <BottomNav page={page} setPage={setPage} t={t} />
        )}
      </div>
    </AppContext.Provider>
  );
}
